<template>
  <div>
    <el-form-item
      class="cpa-setting-header"
      :error="errors.auto_cpa_setting_attributes"
    >
      Auto CPA Settings
    </el-form-item>
    <div class="cpa-settings">
      <div class="block-title el-form-item__content">Test Settings</div>
      <div class="block-description">
        The auto CPA creative launch will start with a test. The test is
        necessary to obtain the data for the further calculation of СPM. During
        the test, traffic will be bought at the optimal price within the set
        budget. After the total testing budget is spent, the CPM automatic
        calculation starts.
      </div>
      <el-form-item :error="errors.total_budget">
        <template #label>
          <div>
            Total Test Budget
            <IconWithHint>
              Total Budget is the amount you are ready to spend to test
              <br />traffic sources and collect data. On this data CPM rates<br />
              will recalculate automatically.
            </IconWithHint>
          </div>
        </template>
        <el-input
          v-model="setting.total_budget"
          type="number"
          :sm="8"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item :error="errors.hourly_budget">
        <template #label>
          <div>
            Hourly Test Budget
            <IconWithHint>
              Hourly Budget is the amount you are ready to spend per hour<br />
              during the test period.
            </IconWithHint>
          </div>
        </template>
        <el-input
          v-model="setting.hourly_budget"
          type="number"
          @update:modelValue="input"
        />
      </el-form-item>
      <CountryPrices
        v-model="prices"
        :win-prices="winPrices"
        :use-historical-prices="useHistoricalPrices"
        @update:modelValue="input"
        @recommendations-change="recommendationsChange"
        @validation-state-change="validationStateChange"
      />
    </div>

    <div v-show="calcVisible" class="cpa-settings">
      <div class="block-title el-form-item__content">
        Recirculation Settings (admins only)
      </div>
      <div class="block-description">
        After the total testing budget is spent, the CPM automatic calculation
        starts and runs every 3/6/12/24 or 48 hours (specified in the
        "Recalculation Frequency" field). If for the period selected in
        "Historical Data Days" your profit exceeds the percentages indicated in
        the "Desired Margin" field, then the CPM increases and you start buying
        more traffic. Otherwise, CPM drops and you start buying less traffic.
        Calculations are appropriate for each country and spot if for the period
        "Days of Historical Data" there were more impressions than the number
        specified in the "Representative Dataset" field.
      </div>
      <el-form-item :error="errors.desired_margin">
        <template #label>
          <div>
            Desired Margin
            <IconWithHint>
              Desired Margin is a percent of media buyer's income that<br />
              will be taken into account when calculating the CPM.
            </IconWithHint>
          </div>
        </template>
        <el-input
          v-model="setting.desired_margin"
          type="number"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item :error="errors.representative_dataset">
        <template #label>
          <div>
            Representative Dataset
            <IconWithHint>
              Representative Dataset is the minimal amount of the impressions<br />
              needed for each country and each spot to calculate its CPM<br />
              according to this data. We recommend setting up 10000+ impressions
            </IconWithHint>
          </div>
        </template>
        <el-input
          v-model="setting.representative_dataset"
          type="number"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item :error="errors.historical_data_days">
        <template #label>
          <div>
            Historical Data Days
            <IconWithHint>
              Historical Data Days is the number of previous days<br />
              that will be taken into account. Based on these days<br />
              statistics the CPM will be automatically calculated.<br />
              We recommend setting up 5 to 10 days.
            </IconWithHint>
          </div>
        </template>
        <el-input
          v-model="setting.historical_data_days"
          type="number"
          @update:modelValue="input"
        />
      </el-form-item>
      <el-form-item
        :error="errors.recalculation_frequency"
        label="Recalculation Frequency"
      >
        <template #label>
          <div>
            Recalculation Frequency
            <IconWithHint>
              Recalculation Frequency is the value how often the system
              recuperate CPM.
            </IconWithHint>
          </div>
        </template>
        <el-select
          v-model="setting.recalculation_frequency"
          @update:modelValue="input"
        >
          <el-option
            v-for="frequency in RecalculationFrequencyLabels"
            :key="frequency.label"
            :label="frequency.label"
            :value="frequency.id"
          >
            {{ frequency.label }}
          </el-option>
        </el-select>
      </el-form-item>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import useValidation from '@/hooks/useValidation'
import {
  RecalculationFrequencyLabels,
  CreativeValidationErrorsKey,
  IAutoCpaSettingAttributes,
  ICountryCPM
} from '@/api/types'
import IconWithHint from '@/components/IconWithHint.vue'
import CountryPrices from './CountryPrices.vue'
import { useUserStore } from '@/store/modules/user'
import type { CountryCpmRecommendation } from '@/apps/traforama/views/creatives/utils/cpm'

export default defineComponent({
  name: 'AutoCpaSetting',
  components: {
    IconWithHint,
    CountryPrices
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    creaturePrices: {
      type: Array,
      required: true
    },
    winPrices: {
      type: Array,
      default: () => []
    },
    useHistoricalPrices: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()
    const { validationErrors } = useValidation(CreativeValidationErrorsKey)
    const errors = validationErrors()
    const setting = ref<IAutoCpaSettingAttributes | any>({})
    setting.value = { ...props.modelValue }
    const prices = ref<Array<ICountryCPM> | any>([])
    prices.value = props.creaturePrices
    const calcVisible = userStore.staff

    function input(newValue: any) {
      context.emit('update', setting.value, prices.value)
    }

    function recommendationsChange(
      recommendations: CountryCpmRecommendation[]
    ) {
      context.emit('recommendations-change', recommendations)
    }

    function validationStateChange(invalid: boolean) {
      context.emit('validation-state-change', invalid)
    }

    return {
      errors,
      setting,
      input,
      prices,
      recommendationsChange,
      validationStateChange,
      RecalculationFrequencyLabels,
      calcVisible
    }
  }
})
</script>

<style lang="scss">
.cpa-setting-header {
  margin-bottom: 0;
  .el-form-item__content {
    border-top: 1px solid var(--el-border-color);
    .el-form-item__error {
      display: inline-block;
      position: relative;
      padding-left: 10px;
    }
  }
}
.cpa-setting-bottom {
  .el-form-item__content {
    border-bottom: 1px solid var(--el-border-color);
  }
}
.block-title {
  margin-left: 15px;
  font-weight: bold;
  color: #485360;
}
.block-description {
  color: #1f2d3d;
  font-size: 14px;
  padding: 0 25px 15px 200px;
}
.cpa-settings {
  border: 2px solid var(--el-border-color);
  margin-bottom: 10px;
  .el-input {
    width: 80% !important;
  }
}
</style>
